import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Plus, Minus, Clock, ShieldCheck, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/apiBase";
import { LOGO_URL } from "@/data/content";

const money = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const prettyDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function Checkout() {
  const { reservationId } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [extraHours, setExtraHours] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API_BASE}/book/${reservationId}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Booking not found"));
  }, [reservationId]);
  useEffect(() => { load(); }, [load]);

  const setQty = (id, qty) => setSelected((s) => ({ ...s, [id]: Math.max(0, Math.min(qty, 10)) }));

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink px-5">
        <div className="card-line max-w-md rounded-2xl bg-white p-10 text-center">
          <p className="font-display text-xl font-bold text-offwhite" data-testid="checkout-error">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="grid min-h-screen place-items-center bg-ink"><Loader2 className="animate-spin text-aqua" /></div>;

  const r = data.reservation;
  const paid = params.get("paid") === "1" || r.payment_status === "paid";
  const rentalDue = Math.round(r.amount_due * 100);
  const extraCents = Math.round(extraHours * r.rate * 100);
  const chosen = data.upsells.filter((u) => (selected[u.upsell_id] || 0) > 0);
  const upsellCents = chosen.reduce((s, u) => s + Math.round(u.price * 100) * selected[u.upsell_id], 0);
  const taxCents = chosen.reduce((s, u) => s + Math.round(u.price * 100 * (u.tax_pct / 100)) * selected[u.upsell_id], 0);
  const total = rentalDue + extraCents + upsellCents + taxCents;

  const pay = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API_BASE}/book/${reservationId}/checkout`, {
        items: chosen.map((u) => ({ upsell_id: u.upsell_id, qty: selected[u.upsell_id] })),
        extra_hours: extraHours,
        origin: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink pb-24" data-testid="checkout-page">
      <header className="border-b border-black/[0.08] bg-white/80 backdrop-blur">
        <div className="container-x flex h-20 items-center justify-between">
          <img src={LOGO_URL} alt="Xtreme Exotics Watersports" className="h-12 w-auto" />
          <span className="flex items-center gap-1.5 text-xs font-semibold text-muted"><ShieldCheck size={14} className="text-aqua" /> Secure XE checkout</span>
        </div>
      </header>

      <main className="container-x max-w-3xl py-10">
        {paid && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-400/10 p-5" data-testid="paid-banner">
            <CheckCircle2 size={22} className="text-emerald-700" />
            <p className="text-sm font-semibold text-offwhite">Payment received — your booking is confirmed. See you on the water!</p>
          </div>
        )}

        <div className="card-line rounded-2xl bg-white p-6 sm:p-8">
          <p className="eyebrow mb-2">Your Jet Car Rental</p>
          <h1 className="font-display text-2xl font-extrabold text-offwhite" data-testid="checkout-vehicle">{r.vehicle_name}</h1>
          <p className="mt-1 text-sm text-muted">{prettyDate(r.date)} · {r.start_time} – {r.end_time} · {r.duration_hours + r.extensions_hours} hours · {r.rate_season} rate ${r.rate}/hr</p>

          {!paid && (
            <>
              <div className="mt-6 border-t border-black/[0.08] pt-6">
                <h2 className="mb-1 font-display text-base font-bold text-offwhite">Available Extras at This Location</h2>
                <p className="mb-4 text-xs text-muted">Approved for your reservation date. Add or remove freely before payment.</p>

                <div className="card-line mb-4 flex items-center justify-between gap-4 rounded-xl p-4" data-testid="extra-hours-row">
                  <div>
                    <p className="text-sm font-bold text-offwhite">Additional Jet Car Hours</p>
                    <p className="text-xs text-muted">${r.rate}/hr · subject to availability · counts toward rental revenue</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setExtraHours(Math.max(0, extraHours - 1))} className="grid h-9 w-9 place-items-center rounded-full border border-black/15 text-offwhite" data-testid="hours-minus" aria-label="Fewer hours"><Minus size={15} /></button>
                    <span className="w-8 text-center font-display text-lg font-bold text-offwhite" data-testid="extra-hours-count">{extraHours}</span>
                    <button onClick={() => setExtraHours(Math.min(data.max_extra_hours, extraHours + 1))} className="grid h-9 w-9 place-items-center rounded-full border border-black/15 text-offwhite" data-testid="hours-plus" aria-label="More hours"><Plus size={15} /></button>
                  </div>
                </div>

                {data.upsells.length === 0 && <p className="text-xs text-muted/70">No add-ons available for this date.</p>}
                <div className="space-y-3">
                  {data.upsells.map((u) => {
                    const qty = selected[u.upsell_id] || 0;
                    return (
                      <div key={u.upsell_id} className={`card-line rounded-xl p-4 transition-colors ${qty > 0 ? "border-aqua/50 bg-aqua/[0.06]" : ""}`} data-testid={`upsell-${u.upsell_id}`}>
                        <div className="flex items-start justify-between gap-4">
                          {u.images?.[0] && <img src={u.images[0]} alt={u.name} className="h-16 w-16 shrink-0 rounded-lg object-cover" />}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-offwhite">{u.name}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-muted">{u.description}</p>
                            {u.notices && <p className="mt-1.5 text-[11px] leading-relaxed text-orange-700">{u.notices}</p>}
                            {u.delivery_timeframe && <p className="mt-1 flex items-center gap-1 text-[11px] text-muted"><Clock size={11} /> {u.delivery_timeframe}</p>}
                          </div>
                          <div className="text-right">
                            <p className="font-display text-lg font-extrabold text-offwhite">${u.price}</p>
                            <div className="mt-1 flex items-center gap-2">
                              <button onClick={() => setQty(u.upsell_id, qty - 1)} className="grid h-8 w-8 place-items-center rounded-full border border-black/15 text-offwhite" data-testid={`upsell-minus-${u.upsell_id}`} aria-label="Remove one"><Minus size={13} /></button>
                              <span className="w-6 text-center font-display text-base font-bold text-offwhite">{qty}</span>
                              <button onClick={() => setQty(u.upsell_id, qty + 1)} className="grid h-8 w-8 place-items-center rounded-full border border-aqua bg-aqua/10 text-aqua" data-testid={`upsell-plus-${u.upsell_id}`} aria-label="Add one"><Plus size={13} /></button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-black/[0.08] bg-ink/50 p-5" data-testid="order-summary">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Order summary</p>
                <div className="space-y-1.5 text-sm">
                  {rentalDue > 0 && <div className="flex justify-between"><span className="text-muted">Jet Car rental ({r.duration_hours}h @ ${r.rate}/hr)</span><span className="font-semibold text-offwhite">{money(rentalDue)}</span></div>}
                  {extraCents > 0 && <div className="flex justify-between"><span className="text-muted">Additional hours (+{extraHours}h)</span><span className="font-semibold text-offwhite">{money(extraCents)}</span></div>}
                  {chosen.map((u) => (
                    <div key={u.upsell_id} className="flex justify-between">
                      <span className="text-muted">{u.name}{selected[u.upsell_id] > 1 ? ` × ${selected[u.upsell_id]}` : ""}</span>
                      <span className="font-semibold text-offwhite">{money(Math.round(u.price * 100) * selected[u.upsell_id])}</span>
                    </div>
                  ))}
                  {taxCents > 0 && <div className="flex justify-between"><span className="text-muted">Taxes</span><span className="font-semibold text-offwhite">{money(taxCents)}</span></div>}
                  <div className="flex justify-between border-t border-black/10 pt-2.5">
                    <span className="font-display font-bold text-offwhite">Total due today</span>
                    <span className="font-display text-xl font-extrabold text-aqua" data-testid="order-total">{money(total)}</span>
                  </div>
                </div>
              </div>

              <button onClick={pay} disabled={busy || total <= 0} className="btn-primary mt-6 w-full disabled:opacity-50" data-testid="pay-now-btn">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <>Pay Securely with Stripe — {money(total)}</>}
              </button>
              <p className="mt-3 text-center text-[11px] text-muted/70">One secure checkout · each item appears separately on your receipt</p>
            </>
          )}
          {paid && (
            <div className="mt-6 border-t border-black/[0.08] pt-6 text-sm text-muted" data-testid="paid-summary">
              <p>Rental total: <strong className="text-offwhite">${r.price}</strong> — paid.</p>
              <p className="mt-1">Questions? Call 352-791-0585.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
